'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { Button, buttonVariants } from '@/components/ui/button'
import { reportError } from '@/lib/monitoring'

/**
 * The panel's error boundary, for every screen under the root layout. Says nothing about what
 * failed — the details go to monitoring — and offers the two ways on: render the screen again, or
 * go back to the overview. A throw in the root layout itself is global-error.tsx's.
 */
export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
    useEffect(() => {
        // The console as well as Sentry: without a DSN it is the only place this error is written down.
        console.error('Unhandled admin error', error)
        reportError(error, { boundary: 'error-page' })
    }, [error])

    return (
        <div className="grid min-h-dvh place-items-center bg-background p-6 text-center">
            <div className="max-w-sm">
                <h1 className="text-2xl">Something went wrong</h1>
                <p className="mt-2 leading-relaxed text-muted-foreground">
                    This screen failed to render. Try again; if it keeps failing, the reference below finds it in the error reports.
                </p>
                {error.digest && <p className="mt-3 font-mono text-xs text-muted-foreground">Reference: {error.digest}</p>}
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                    <Button onClick={reset}>Try again</Button>
                    <Link href="/" className={buttonVariants({ variant: 'outline' })}>Back to the overview</Link>
                </div>
            </div>
        </div>
    )
}
